import { useEffect, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Calendar, Gauge, Battery, Euro, Star, Clock, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function HomePage() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [premiumListings, setPremiumListings] = useState([]);
  const [latestListings, setLatestListings] = useState([]);
  const [dealOfDay, setDealOfDay] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    brand_id: '',
    model_id: '',
    price_min: '',
    price_max: '',
    year_min: '',
    year_max: '',
    mileage_min: '',
    mileage_max: '',
    body_type: '',
    power_min: '',
    power_max: '',
    drive_type: '',
    battery_included: null,
    vat_deductible: null,
    equipment_ids: []
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [brandsRes, equipmentRes, premiumRes, latestRes, dealRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/brands`),
        fetch(`${BACKEND_URL}/api/equipment`),
        fetch(`${BACKEND_URL}/api/listings/premium`),
        fetch(`${BACKEND_URL}/api/listings/latest`),
        fetch(`${BACKEND_URL}/api/listings/deal-of-day`)
      ]);

      const brandsData = await brandsRes.json();
      const equipmentData = await equipmentRes.json();
      const premiumData = await premiumRes.json();
      const latestData = await latestRes.json();
      const dealData = await dealRes.json();

      setBrands(brandsData);
      setEquipment(equipmentData);
      setPremiumListings(premiumData);
      setLatestListings(latestData);
      setDealOfDay(dealData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoading(false);
    }
  };

  const loadModels = async (brandId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/models?brand_id=${brandId}`);
      const data = await res.json();
      setModels(data);
    } catch (error) {
      console.error('Failed to load models:', error);
    }
  };

  const handleBrandChange = (brandId) => {
    setFilters({ ...filters, brand_id: brandId || '', model_id: '' });
    if (brandId) {
      loadModels(brandId);
    } else {
      setModels([]);
    }
  };

  const handleSearch = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && !(Array.isArray(value) && value.length === 0)) {
          if (key === 'equipment_ids') {
            params.append(key, value.join(','));
          } else {
            params.append(key, value);
          }
        }
      });

      const res = await fetch(`${BACKEND_URL}/api/listings?${params.toString()}`);
      const data = await res.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleEquipmentToggle = (equipmentId) => {
    const current = filters.equipment_ids || [];
    if (current.includes(equipmentId)) {
      setFilters({ ...filters, equipment_ids: current.filter(id => id !== equipmentId) });
    } else {
      setFilters({ ...filters, equipment_ids: [...current, equipmentId] });
    }
  };

  const calculateNetPrice = (price) => {
    return (price / 1.23).toFixed(2);
  };

  const ListingCard = ({ listing, type = "normal" }) => (
    <Card
      data-testid={`listing-card-${listing.listing_id}`}
      onClick={() => navigate(`/listing/${listing.listing_id}`)}
      className={`group relative bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer ${
        type === "premium" ? 'border-amber-400 shadow-md' : 
        type === "deal" ? 'border-green-500 shadow-lg' : 'border-slate-200'
      }`}
    >
      {type === "premium" && (
        <Badge className="absolute top-2 right-2 z-10 bg-amber-500 text-white" data-testid="premium-badge">
          <Star className="h-3 w-3 mr-1" />
          Premium
        </Badge>
      )}
      {type === "deal" && (
        <Badge className="absolute top-2 right-2 z-10 bg-green-600 text-white" data-testid="deal-badge">
          <Crown className="h-3 w-3 mr-1" />
          Ponuka dňa
        </Badge>
      )}
      <div className={`${type === "deal" ? "aspect-[16/9]" : "aspect-video"} bg-slate-100 relative overflow-hidden`}>
        {listing.images && listing.images.length > 0 ? (
          <img
            src={listing.images[0]}
            alt={`${listing.brand_name} ${listing.model_name}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Battery className="h-16 w-16 text-slate-300" />
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className={`font-bold text-primary mb-1 ${type === "deal" ? "text-2xl" : "text-lg"}`} style={{fontFamily: 'Manrope, sans-serif'}} data-testid="listing-title">
          {listing.brand_name} {listing.model_name}
        </h3>
        <p className={`font-bold text-accent mb-3 ${type === "deal" ? "text-3xl" : "text-xl"}`} style={{fontFamily: 'Manrope, sans-serif'}} data-testid="listing-price">
          €{listing.price.toLocaleString()}
        </p>
        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {listing.year}
          </div>
          <div className="flex items-center gap-1">
            <Gauge className="h-4 w-4" />
            {listing.mileage.toLocaleString()} km
          </div>
          <div className="flex items-center gap-1">
            <Battery className="h-4 w-4" />
            {listing.power} kW
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {listing.city || listing.body_type}
          </div>
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <div className="bg-gradient-to-b from-slate-50 to-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Ponuka dňa - Deal of the Day */}
            <div className="mb-8" data-testid="deal-of-day-section">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="h-6 w-6 text-green-600" />
                <h2 className="text-2xl font-bold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Ponuka dňa</h2>
              </div>
              {dealOfDay ? (
                <div className="max-w-2xl">
                  <ListingCard listing={dealOfDay} type="deal" />
                </div>
              ) : (
                <Card className="p-8 text-center bg-green-50 border-green-200">
                  <Crown className="h-12 w-12 text-green-300 mx-auto mb-3" />
                  <p className="text-slate-600">Momentálne nie je aktívna žiadna ponuka dňa</p>
                </Card>
              )}
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              {/* Ľavý stĺpec - Vyhľadávanie */}
              <div className="lg:w-2/5" data-testid="search-filters-section">
                <Card className="p-6 bg-green-50 border border-green-200 rounded-xl lg:sticky lg:top-20">
                  <div className="flex items-center gap-2 mb-6">
                    <Search className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Detailé vyhľadávanie</h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Výrobca</Label>
                      <Select 
                        key={`brand-select-${brands.length}`}
                        value={filters.brand_id || undefined} 
                        onValueChange={handleBrandChange}
                      >
                        <SelectTrigger data-testid="brand-select" className="bg-white touch-manipulation">
                          <SelectValue placeholder="Vyberte značku" />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          {brands.length > 0 ? (
                            brands.map(brand => (
                              <SelectItem key={brand.brand_id} value={brand.brand_id}>{brand.name}</SelectItem>
                            ))
                          ) : (
                            <div className="px-2 py-1.5 text-sm text-slate-500">Načítavam...</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Model</Label>
                      <Select 
                        key={`model-select-${models.length}`}
                        value={filters.model_id || undefined} 
                        onValueChange={(val) => setFilters({...filters, model_id: val})} 
                        disabled={!filters.brand_id}
                      >
                        <SelectTrigger data-testid="model-select" className="bg-white touch-manipulation">
                          <SelectValue placeholder="Vyberte model" />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          {models.length > 0 ? (
                            models.map(model => (
                              <SelectItem key={model.model_id} value={model.model_id}>{model.name}</SelectItem>
                            ))
                          ) : (
                            <div className="px-2 py-1.5 text-sm text-slate-500">
                              {filters.brand_id ? 'Načítavam...' : 'Najprv vyberte značku'}
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Cena (€)</Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="price-min-input"
                          type="number"
                          placeholder="Od"
                          value={filters.price_min}
                          onChange={(e) => setFilters({...filters, price_min: e.target.value})}
                          className="bg-white"
                        />
                        <Input
                          data-testid="price-max-input"
                          type="number"
                          placeholder="Do"
                          value={filters.price_max}
                          onChange={(e) => setFilters({...filters, price_max: e.target.value})}
                          className="bg-white"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Rok výroby</Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="year-min-input"
                          type="number"
                          placeholder="Od"
                          value={filters.year_min}
                          onChange={(e) => setFilters({...filters, year_min: e.target.value})}
                          className="bg-white"
                        />
                        <Input
                          data-testid="year-max-input"
                          type="number"
                          placeholder="Do"
                          value={filters.year_max}
                          onChange={(e) => setFilters({...filters, year_max: e.target.value})}
                          className="bg-white"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Počet km</Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="mileage-min-input"
                          type="number"
                          placeholder="Od"
                          value={filters.mileage_min}
                          onChange={(e) => setFilters({...filters, mileage_min: e.target.value})}
                          className="bg-white"
                        />
                        <Input
                          data-testid="mileage-max-input"
                          type="number"
                          placeholder="Do"
                          value={filters.mileage_max}
                          onChange={(e) => setFilters({...filters, mileage_max: e.target.value})}
                          className="bg-white"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Typ karosérie</Label>
                      <Select value={filters.body_type || undefined} onValueChange={(val) => setFilters({...filters, body_type: val})}>
                        <SelectTrigger data-testid="body-type-select" className="bg-white touch-manipulation">
                          <SelectValue placeholder="Všetky typy" />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          <SelectItem value="Sedan">Sedan</SelectItem>
                          <SelectItem value="SUV">SUV</SelectItem>
                          <SelectItem value="Hatchback">Hatchback</SelectItem>
                          <SelectItem value="Coupe">Coupe</SelectItem>
                          <SelectItem value="Wagon">Wagon</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Výkon (kW)</Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="power-min-input"
                          type="number"
                          placeholder="Od"
                          value={filters.power_min}
                          onChange={(e) => setFilters({...filters, power_min: e.target.value})}
                          className="bg-white"
                        />
                        <Input
                          data-testid="power-max-input"
                          type="number"
                          placeholder="Do"
                          value={filters.power_max}
                          onChange={(e) => setFilters({...filters, power_max: e.target.value})}
                          className="bg-white"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700">Pohon</Label>
                      <Select value={filters.drive_type || undefined} onValueChange={(val) => setFilters({...filters, drive_type: val})}>
                        <SelectTrigger data-testid="drive-type-select" className="bg-white touch-manipulation">
                          <SelectValue placeholder="Všetky typy" />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4}>
                          <SelectItem value="Predný">Predný</SelectItem>
                          <SelectItem value="Zadný">Zadný</SelectItem>
                          <SelectItem value="4x4">4x4</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          data-testid="battery-checkbox"
                          id="battery"
                          checked={filters.battery_included === true}
                          onCheckedChange={(checked) => setFilters({...filters, battery_included: checked ? true : null})}
                        />
                        <Label htmlFor="battery" className="text-sm cursor-pointer">S batériou</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          data-testid="vat-checkbox"
                          id="vat"
                          checked={filters.vat_deductible === true}
                          onCheckedChange={(checked) => setFilters({...filters, vat_deductible: checked ? true : null})}
                        />
                        <Label htmlFor="vat" className="text-sm cursor-pointer">Možnosť odpočtu DPH (23%)</Label>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">Výbava</Label>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 max-h-48 overflow-y-auto pr-2">
                        {equipment.map(eq => (
                          <div key={eq.equipment_id} className="flex items-center space-x-2">
                            <Checkbox
                              data-testid={`equipment-checkbox-${eq.equipment_id}`}
                              id={eq.equipment_id}
                              checked={filters.equipment_ids.includes(eq.equipment_id)}
                              onCheckedChange={() => handleEquipmentToggle(eq.equipment_id)}
                            />
                            <Label htmlFor={eq.equipment_id} className="text-sm cursor-pointer">{eq.name}</Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      data-testid="search-button"
                      onClick={handleSearch}
                      className="w-full bg-primary text-white hover:bg-slate-800 font-semibold"
                    >
                      Vyhľadať
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Pravý stĺpec - Premium inzeráty */}
              <div className="lg:w-3/5">
                {searchResults.length > 0 ? (
                  <div data-testid="search-results-section">
                    <h2 className="text-2xl font-semibold text-primary mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
                      Výsledky vyhľadávania ({searchResults.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {searchResults.map(listing => (
                        <ListingCard key={listing.listing_id} listing={listing} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div data-testid="premium-listings-section">
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="h-5 w-5 text-amber-500" />
                      <h2 className="text-xl font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Premium inzeráty</h2>
                    </div>
                    {premiumListings.length > 0 ? (
                      <div className="grid grid-cols-2 gap-4">
                        {premiumListings.slice(0, 10).map(listing => (
                          <ListingCard key={listing.listing_id} listing={listing} type="premium" />
                        ))}
                      </div>
                    ) : (
                      <Card className="p-8 text-center bg-amber-50 border-amber-200">
                        <Star className="h-12 w-12 text-amber-300 mx-auto mb-3" />
                        <p className="text-slate-600">Momentálne nie sú žiadne premium inzeráty</p>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Najnovšie inzeráty - pod vyhľadávaním */}
            <div className="mt-12" data-testid="latest-listings-section">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-semibold text-primary" style={{fontFamily: 'Manrope, sans-serif'}}>Najnovšie inzeráty</h2>
              </div>
              {latestListings.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {latestListings.map(listing => (
                    <ListingCard key={listing.listing_id} listing={listing} />
                  ))}
                </div>
              ) : (
                <Card className="p-8 text-center bg-slate-50">
                  <Battery className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">Zatiaľ neboli pridané žiadne inzeráty</p>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default HomePage;
